import * as R from 'ramda'
import { useCallback } from 'react'
import {
  MarkChatReadOutlined,
  MarkChatUnreadOutlined,
} from '@mui/icons-material'
// Common
import { PopupMenuItem, PopupMenuList } from '$common/popup-menu'
import { PinIcon } from '$common/icons'
import { BlockIcon } from '$common/icons/block'
import { ClearHistoryIcon } from '$common/icons/clear-history'
// Logic
import { RoomList } from '$logic/room-list/room-list.react-hook'
import { getRoom, markRoomAsRead, markRoomAsUnread } from '$logic/room'
import { getRoomUnseenCountAndUnreadMark } from '$logic/room-seen'
import { useAsyncFunction } from '$func/async-function.react-hook'
// Localization
import { PINNED_ROOMS_COUNT, getPinnedRooms, pinRooms, unPinRooms } from '$logic/user-preferences'
import { blockUser } from '$logic/user-policy'
import { getUserIdsByPvIds } from '$logic/pv'
import { getUser } from '$logic/users'
import { t } from '$i18n'
import { showClearAllPVsHistory } from '$comp/modals/confirmation-dialog'
import { showPinRoomLimitSnackbar } from '$comp/snackbars'



// TODO: Implement logic later
async function getRoomListMenuUsers(roomIds: string[]) {
  const rooms = await Promise.all(roomIds.map(getRoom))
  return R.all(R.propEq('type', 'PV'), rooms)
}

interface Props {
  isOpen: boolean
  chatItems: RoomList[]
  onClose: () => unknown
}
export function ChatListMenu({ chatItems, onClose, isOpen }: Props) {
  const canPinRooms = R.reject(R.prop('pinned'), chatItems)
  const canUnPinRooms = R.filter(R.prop('pinned'), chatItems)

  const canPin = canPinRooms.length && canPinRooms.length < PINNED_ROOMS_COUNT
  const canUnPin = canUnPinRooms.length

  const { loading, value } = useAsyncFunction(
    () =>
      Promise.all(
        chatItems.map(({ chatId }) => getRoomUnseenCountAndUnreadMark(chatId)),
      ),
    [chatItems],
  )

  const { value: areAllUsers } = useAsyncFunction(
    () => getRoomListMenuUsers(chatItems.map(R.prop('chatId'))),
    [chatItems],
  )

  const canMarkAsRead =
    !loading &&
    R.any(
      ({ unseenCount, unreadMark }) => Boolean(unseenCount || unreadMark),
      value,
    )

  const canMarkAsUnread = !canMarkAsRead && !loading

  const onPin = async () => {
    const pinnedRooms = await getPinnedRooms()
    if (pinnedRooms.length + canPinRooms.length <= PINNED_ROOMS_COUNT) {
      analite('CHATLIST_PIN_CHAT', { active: true })
      await pinRooms(canPinRooms.map(R.prop('chatId')))
    } else showPinRoomLimitSnackbar()
    onClose()
  }

  const onUnPin = async () => {
    analite('CHATLIST_PIN_CHAT', { active: false })
    await unPinRooms(canUnPinRooms.map(R.prop('chatId')))
    onClose()
  }

  const onBlock = async () => {
    const selectedChatsUserIds = await getUserIdsByPvIds(
      R.pluck('chatId', chatItems),
    )
    const selectedUsersList = await Promise.all(
      selectedChatsUserIds.map(getUser),
    )
    await Promise.all(
      selectedUsersList
        .filter(user => !user.isBlocked)
        .map(({ id }) => blockUser(id)),
    )
    analite('BLOCK_USER', {
      active: true,
      source: 'CHAT_LIST',
    })
    onClose()
  }

  const onClearHistory = async () => {
    await showClearAllPVsHistory(R.pluck('chatId', chatItems))
    onClose()
  }

  const onMarkAsRead = useCallback(async () => {
    await Promise.all(chatItems.map(({ chatId }) => markRoomAsRead(chatId)))
    await onClose()
  }, [chatItems])

  const onMarkAsUnread = useCallback(async () => {
    await Promise.all(chatItems.map(({ chatId }) => markRoomAsUnread(chatId)))
    await onClose()
  }, [chatItems])

  return (
    <PopupMenuList open={isOpen}>
      {canPin && (
        <PopupMenuItem text={t('chatList.menu.pin')} onClick={onPin}>
          <PinIcon sx={{ color: 'secondary.main' }} />
        </PopupMenuItem>
      )}

      {canUnPin && !canPin && (
        <PopupMenuItem text={t('chatList.menu.unPin')} onClick={onUnPin}>
          <PinIcon sx={{ color: 'secondary.main' }} />
        </PopupMenuItem>
      )}

      {areAllUsers && (
        <PopupMenuItem text={t('profile.user.block')} onClick={onBlock}>
          <BlockIcon sx={{ color: 'secondary.main' }} />
        </PopupMenuItem>
      )}

      {areAllUsers && (
        <PopupMenuItem
          text={t('menu.common.clearHistory')}
          onClick={onClearHistory}
        >
          <ClearHistoryIcon />
        </PopupMenuItem>
      )}

      {canMarkAsRead && (
        <PopupMenuItem
          text={t('menu.common.markAsRead')}
          onClick={onMarkAsRead}
        >
          <MarkChatReadOutlined
            viewBox="0 0 24 24"
            sx={{ color: 'secondary.main' }}
          />
        </PopupMenuItem>
      )}
      {canMarkAsUnread && (
        <PopupMenuItem
          text={t('menu.common.markAsUnread')}
          onClick={onMarkAsUnread}
        >
          <MarkChatUnreadOutlined sx={{ color: 'secondary.main' }} />
        </PopupMenuItem>
      )}
    </PopupMenuList>
  )
}
